import {
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
  taskStatusColor,
} from "./skillFichaHelpers";

const SIN_TEMPORADA = "Sin temporada";
const SIN_CLIENTE = "Sin cliente";

export function taskClientLabel(row) {
  return (
    row?.CustomerName ||
    (row?.CustomerId ? `Cliente #${row.CustomerId}` : null) ||
    row?.SkillSlug ||
    SIN_CLIENTE
  );
}

export function taskSeasonLabel(row) {
  const t = String(row?.Temporada || "").trim();
  return t || SIN_TEMPORADA;
}

export function searchStatusLabel(status) {
  const map = {
    draft: "Borrador",
    running: "Buscando…",
    done: "Listo",
    error: "Error",
    cancelled: "Cancelado",
  };
  return map[status] || status || "—";
}

/**
 * groupBy: "season" → Temporada → Cliente → SEARCHS + tareas
 * groupBy: "client" → Cliente → Temporada → SEARCHS + tareas
 *
 * @param {object[]} rows tasks
 * @param {"season"|"client"} groupBy
 * @param {object[]} searches library searches (same CustomerId/Temporada keys)
 */
export function buildAgentTasksTree(rows = [], groupBy = "season", searches = []) {
  const filtered = Array.isArray(rows) ? rows : [];
  const searchRows = Array.isArray(searches) ? searches : [];
  const rootMap = new Map();

  function ensureL2(L1, L2, kinds) {
    if (!rootMap.has(L1)) {
      rootMap.set(L1, {
        id: `l1:${L1}`,
        label: L1,
        kind: kinds.l1,
        children: new Map(),
        tasks: [],
      });
    }
    const n1 = rootMap.get(L1);
    if (!n1.children.has(L2)) {
      n1.children.set(L2, {
        id: `l2:${L1}::${L2}`,
        label: L2,
        kind: kinds.l2,
        tasks: [],
        searches: [],
      });
    }
    return n1.children.get(L2);
  }

  const kinds =
    groupBy === "client"
      ? { l1: "client", l2: "season" }
      : { l1: "season", l2: "client" };

  for (const row of filtered) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).tasks.push(row);
  }

  for (const row of searchRows) {
    const season = taskSeasonLabel(row);
    const client = taskClientLabel(row);
    const L1 = groupBy === "client" ? client : season;
    const L2 = groupBy === "client" ? season : client;
    ensureL2(L1, L2, kinds).searches.push(row);
  }

  const sortLabel = (a, b) =>
    String(a).localeCompare(String(b), "es", {
      numeric: true,
      sensitivity: "base",
    });

  const roots = [...rootMap.values()]
    .map((n1) => {
      const children = [...n1.children.values()]
        .map((n2) => {
          const tasks = [...n2.tasks].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const searchesSorted = [...(n2.searches || [])].sort((a, b) =>
            String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")),
          );
          const searchsNode = {
            id: `${n2.id}::searchs`,
            label: "SEARCHS",
            kind: "searchs",
            searches: searchesSorted,
            tasks: [],
            count: searchesSorted.length,
            seasonLabel: groupBy === "client" ? n2.label : n1.label,
            clientLabel: groupBy === "client" ? n1.label : n2.label,
            // Context for "Nuevo search" under this branch
            Temporada: groupBy === "client" ? n2.label : n1.label,
            CustomerName: groupBy === "client" ? n1.label : n2.label,
            CustomerId:
              tasks.find((t) => t.CustomerId)?.CustomerId ||
              searchesSorted.find((s) => s.CustomerId)?.CustomerId ||
              null,
          };
          return {
            ...n2,
            tasks,
            searches: searchesSorted,
            searchsNode,
            count: tasks.length + searchesSorted.length,
          };
        })
        .sort((a, b) => sortLabel(a.label, b.label));
      const count = children.reduce((s, c) => s + c.count, 0);
      return { ...n1, children, count };
    })
    .sort((a, b) => {
      if (a.label === SIN_TEMPORADA || a.label === SIN_CLIENTE) return 1;
      if (b.label === SIN_TEMPORADA || b.label === SIN_CLIENTE) return -1;
      return sortLabel(a.label, b.label);
    });

  return roots;
}

export { TASK_STATUS_LABELS, TASK_TYPE_LABELS, taskStatusColor, SIN_TEMPORADA, SIN_CLIENTE };
